// Organizations API client (members management)

const BASE_URL = import.meta.env.VITE_WATTELZCE_API_URL || '';

export interface OrgMember {
  membershipId: number;
  orgId: number;
  userId: number;
  userEmail: string;
  firstName: string | null;
  lastName: string | null;
  platformRole: 'super_admin' | 'user';
  isActive: 0 | 1;
  orgRole: 'org_admin' | 'maintenance' | 'user';
  joinedAt: string;
  invitedBy: number | null;
}

export interface OrganizationDetail {
  id: number;
  uuid: string;
  name: string;
  slug: string;
  logoUrl: string | null;
  status: string;
  plan: string | null;
  createdBy: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface InvitePayload {
  email: string;
  role: 'org_admin' | 'maintenance' | 'user';
}

function getHeaders(): HeadersInit {
  const token = localStorage.getItem('dashboard_zce_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers: { ...getHeaders(), ...(options?.headers || {}) },
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.success === false) {
    // Le backend renvoie error sous forme de chaîne OU d'objet { message, code }.
    // On normalise toujours en chaîne pour ne jamais rendre un objet en JSX.
    const rawErr = json.error;
    const msg =
      (typeof rawErr === 'string' ? rawErr : rawErr?.message) || `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return (json.data ?? json) as T;
}

export const organizationsApi = {
  getOrganization: (orgId: number | 'current') =>
    apiFetch<OrganizationDetail>(`/api/organizations/${orgId}`),

  listMembers: (orgId: number | 'current') =>
    apiFetch<OrgMember[]>(`/api/organizations/${orgId}/members`),

  inviteMember: (orgId: number | 'current', payload: InvitePayload) =>
    apiFetch<{ token: string; email: string; orgId: number; role: string; link: string; expiresInDays: number }>(
      `/api/organizations/${orgId}/members`,
      { method: 'POST', body: JSON.stringify(payload) }
    ),

  listInvitations: (orgId: number | 'current') =>
    apiFetch<Array<{
      id: number; token: string; email: string; role: string; status: string;
      expiresAt: string; acceptedAt: string | null; createdAt: string; invitedBy: number;
    }>>(`/api/organizations/${orgId}/invitations`),

  revokeInvitation: (orgId: number | 'current', id: number) =>
    apiFetch<{ success: true }>(
      `/api/organizations/${orgId}/invitations/${id}`,
      { method: 'DELETE' }
    ),

  updateRole: (
    orgId: number | 'current',
    userId: number,
    role: 'org_admin' | 'maintenance' | 'user',
    password: string
  ) =>
    apiFetch<{ orgId: number; userId: number; role: string }>(
      `/api/organizations/${orgId}/members/${userId}`,
      { method: 'PATCH', body: JSON.stringify({ role, password }) }
    ),

  removeMember: (orgId: number | 'current', userId: number, password: string) =>
    apiFetch<{ success: true }>(
      `/api/organizations/${orgId}/members/${userId}`,
      { method: 'DELETE', body: JSON.stringify({ password }) }
    ),
};

// ─── Public APIs ───

export interface InvitationInfo {
  email: string;
  role: 'org_admin' | 'maintenance' | 'user';
  orgName: string;
  orgSlug: string;
  userExists: boolean;
  expiresAt: string;
}

export const invitationsApi = {
  getInvitation: (token: string) =>
    apiFetch<InvitationInfo>(`/api/invitations/${token}`),

  acceptInvitation: (
    token: string,
    payload?: { firstName?: string; lastName?: string; password?: string }
  ) =>
    apiFetch<{ token: string; user: any }>(
      `/api/invitations/${token}/accept`,
      { method: 'POST', body: JSON.stringify(payload || {}) }
    ),

  signupOrg: (payload: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    organizationName: string;
    message?: string;
  }) =>
    apiFetch<{ message: string }>(
      `/api/auth/signup-org`,
      { method: 'POST', body: JSON.stringify(payload) }
    ),
};

export interface SignupRequest {
  id: number;
  uuid: string;
  email: string;
  firstName: string;
  lastName: string;
  organizationName: string;
  organizationSlug: string;
  message: string | null;
  status: 'pending' | 'approved' | 'rejected';
  reviewedBy: number | null;
  reviewNotes: string | null;
  reviewedAt: string | null;
  createdAt: string;
  createdOrgId: number | null;
  createdUserId: number | null;
}

export const adminApi = {
  listSignupRequests: (status: 'pending' | 'approved' | 'rejected' = 'pending') =>
    apiFetch<SignupRequest[]>(`/api/admin/signup-requests?status=${status}`),

  approveSignup: (id: number) =>
    apiFetch<{ orgId: number; userId: number; organizationName: string; slug: string }>(
      `/api/admin/signup-requests/${id}/approve`,
      { method: 'POST', body: JSON.stringify({}) }
    ),

  rejectSignup: (id: number, notes?: string) =>
    apiFetch<{ success: true }>(
      `/api/admin/signup-requests/${id}/reject`,
      { method: 'POST', body: JSON.stringify({ notes }) }
    ),

  listOrganizations: () =>
    apiFetch<Array<{
      id: number; uuid: string; name: string; slug: string; status: string;
      plan: string | null; createdAt: string;
      members: Array<{
        orgId: number; userId: number; orgRole: 'org_admin' | 'maintenance' | 'user';
        joinedAt: string; userEmail: string;
        firstName: string | null; lastName: string | null;
        platformRole: 'super_admin' | 'user';
        isActive: 0 | 1;
      }>;
    }>>(`/api/admin/organizations`),

  deleteOrganization: (orgId: number, password: string) =>
    apiFetch<{ deletedOrgId: number; name: string }>(
      `/api/organizations/${orgId}`,
      { method: 'DELETE', body: JSON.stringify({ password }) }
    ),
};
